import { Controller } from "@hotwired/stimulus"

// ── A lone tree swaying in the wind, drawn procedurally in a fragment shader ───
// Full-screen quad; the fragment shader builds a soft overcast sky, a tapered
// trunk and a ragged, layered canopy. A height-weighted wind field bends the
// whole tree and shimmers the foliage so it reads as wind, not just noise.

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`

const FRAG = `
precision highp float;

uniform vec2  u_res;
uniform float u_time;

// ── Value noise + fbm ─────────────────────────────────────────────────────────
float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.0; a *= 0.5; }
  return v;
}

// Horizontal bend of the tree centreline at normalised height y.
// Higher up bends more (y*y); gusts swell and fade over several seconds.
float treeBend(float y, float t) {
  float s = sin(t * 1.3 + y * 1.6) * 0.6
          + sin(t * 2.1 + y * 2.9) * 0.3
          + sin(t * 0.6)           * 0.5;
  float gust = 0.5 + 0.5 * sin(t * 0.37);
  return s * (0.25 + 0.75 * gust) * 0.07 * y * y;
}

// Ragged foliage mass. Returns (alpha, shade).
vec2 foliage(vec2 p, vec2 center, float rad, float t) {
  vec2 q = p - center;
  q.x /= 1.18;                       // wider than tall
  float r = length(q);
  // Leaf frame un-bends with the trunk and shimmers, so detail rides the wind.
  vec2 lc = (p - vec2(treeBend(p.y, t), 0.0)) * 7.0
          + vec2(sin(t * 0.3), cos(t * 0.25)) * 0.4;
  float n      = fbm(lc);
  float radius = rad + (n - 0.5) * 0.13;
  float a      = smoothstep(radius, radius - 0.025, r);
  float shade  = fbm(lc * 1.6 + 7.0);
  return vec2(a, shade);
}

void main() {
  float aspect = u_res.x / u_res.y;
  vec2  uv = gl_FragCoord.xy / u_res;          // 0..1, y up
  vec2  p  = vec2((uv.x - 0.5) * aspect, uv.y); // tree rooted at x=0, y=0 (bottom)
  float t  = u_time;

  // Soft overcast sky — neutral, calm.
  vec3 col = mix(vec3(0.95, 0.94, 0.91), vec3(0.84, 0.87, 0.90), uv.y);

  // Out-of-focus rolling ground — muted overcast palette, drawn behind the tree.
  vec3 skyHorizon = vec3(0.95, 0.94, 0.91);
  float hy = 0.19 + fbm(vec2(uv.x * 2.2 + t * 0.004, 0.0)) * 0.09;
  float groundA = smoothstep(hy + 0.045, hy - 0.045, uv.y);
  float hFade   = clamp(uv.y / (hy + 0.06), 0.0, 1.0);
  vec3 groundNear = vec3(0.44, 0.50, 0.36);
  vec3 groundFar  = vec3(0.56, 0.60, 0.48);
  vec3 groundCol  = mix(groundNear, groundFar, hFade);
  float patches   = fbm(vec2(uv.x * 3.8 + t * 0.003, 4.1));
  float patchMask = smoothstep(hy - 0.03, hy + 0.04, uv.y);
  groundCol = mix(groundCol, groundCol * 0.62, patches * 0.45 * patchMask);
  groundCol = mix(groundCol, skyHorizon * 0.95, hFade * 0.55);
  col = mix(col, groundCol, groundA);

  // Back canopy (recedes, darker, slightly offset).
  vec2 f0 = foliage(p, vec2(treeBend(0.75, t) - 0.06, 0.70), 0.27, t);
  vec3 backCol = mix(vec3(0.20, 0.28, 0.18), vec3(0.28, 0.36, 0.22), f0.y);
  col = mix(col, backCol, f0.x * 0.9);

  // Trunk — tapered, bends with the wind field.
  float bx     = treeBend(p.y, t);
  float tw     = mix(0.024, 0.005, clamp(p.y / 0.6, 0.0, 1.0));
  float td     = abs(p.x - bx);
  float trunkA = smoothstep(tw, tw - 0.004, td) * smoothstep(0.66, 0.6, p.y);
  vec3  trunkCol = mix(vec3(0.22, 0.18, 0.15), vec3(0.33, 0.27, 0.22),
                       fbm(vec2(p.y * 30.0, p.x * 5.0)));
  col = mix(col, trunkCol, trunkA);

  // Front canopy (covers the trunk top, brighter toward the crown).
  vec2 f1 = foliage(p, vec2(bx, 0.74), 0.31, t);
  vec3 frontCol = mix(vec3(0.30, 0.40, 0.25), vec3(0.45, 0.55, 0.34), f1.y);
  frontCol *= mix(0.72, 1.06, smoothstep(0.45, 0.95, p.y));
  col = mix(col, frontCol, f1.x);

  gl_FragColor = vec4(col, 1.0);
}
`

export default class extends Controller {
  connect() {
    const gl = this.element.getContext("webgl") || this.element.getContext("experimental-webgl")
    if (!gl) return
    this._gl = gl

    this._prog = this._buildProg(gl)
    if (!this._prog) return
    gl.useProgram(this._prog)
    this._setupGeo(gl)

    this._u = {
      res:  gl.getUniformLocation(this._prog, "u_res"),
      time: gl.getUniformLocation(this._prog, "u_time"),
    }

    this._setSize()

    // Respect a reduced-motion preference by holding a calm, static frame.
    this._reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    this._start   = performance.now()

    this._running = true
    this._ro = new ResizeObserver(() => this._setSize())
    this._ro.observe(document.documentElement)
    this._tick()
  }

  disconnect() {
    this._running = false
    this._ro?.disconnect()
    const gl = this._gl
    if (gl) {
      if (this._prog) gl.deleteProgram(this._prog)
      if (this._buf)  gl.deleteBuffer(this._buf)
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  _setSize() {
    const c = this.element
    c.width  = window.innerWidth
    c.height = window.innerHeight
    const gl = this._gl
    if (!gl) return
    gl.viewport(0, 0, c.width, c.height)
    gl.useProgram(this._prog)
    gl.uniform2f(this._u.res, c.width, c.height)
  }

  _tick() {
    if (!this._running) return
    const gl = this._gl
    const t  = this._reduced ? 0 : (performance.now() - this._start) / 1000
    gl.useProgram(this._prog)
    gl.uniform1f(this._u.time, t)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
    if (this._reduced) { this._running = false; return }
    requestAnimationFrame(() => this._tick())
  }

  _buildProg(gl) {
    const vs = this._compile(gl, gl.VERTEX_SHADER,   VERT)
    const fs = this._compile(gl, gl.FRAGMENT_SHADER, FRAG)
    if (!vs || !fs) return null
    const p = gl.createProgram()
    gl.attachShader(p, vs); gl.attachShader(p, fs)
    gl.linkProgram(p)
    gl.deleteShader(vs); gl.deleteShader(fs)
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.error("Tree shader link error:", gl.getProgramInfoLog(p))
      return null
    }
    return p
  }

  _compile(gl, type, src) {
    const s = gl.createShader(type)
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error("Tree shader compile error:", gl.getShaderInfoLog(s))
      gl.deleteShader(s); return null
    }
    return s
  }

  _setupGeo(gl) {
    this._buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this._buf)
    gl.bufferData(gl.ARRAY_BUFFER,
      new Float32Array([-1,-1, 1,-1, -1,1,  -1,1, 1,-1, 1,1]),
      gl.STATIC_DRAW)
    const loc = gl.getAttribLocation(this._prog, "a_pos")
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
  }
}
